import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

const ResetPassword: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [token, setToken] = useState<string>('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [errors, setErrors] = useState<string[]>([]);

    useEffect(() => {
        const tokenParam = searchParams.get('token');
        if (!tokenParam) {
            setMessage({
                type: 'error',
                text: 'Ugyldig tilbakestillingslenke. Vennligst be om en ny lenke.'
            });
        } else {
            setToken(tokenParam);
        }
    }, [searchParams]);

    const validatePassword = (password: string): string[] => {
        const errors: string[] = [];

        if (password.length < 8) {
            errors.push('Passordet må være minst 8 tegn langt');
        }
        if (!/[a-z]/.test(password)) {
            errors.push('Passordet må inneholde minst én liten bokstav');
        }
        if (!/[A-Z]/.test(password)) {
            errors.push('Passordet må inneholde minst én stor bokstav');
        }
        if (!/\d/.test(password)) {
            errors.push('Passordet må inneholde minst ett tall');
        }
        if (!/[@$!%*?&]/.test(password)) {
            errors.push('Passordet må inneholde minst ett spesialtegn (@$!%*?&)');
        }

        return errors;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setMessage(null);
        setErrors([]);

        // Validate passwords match
        if (newPassword !== confirmPassword) {
            setMessage({
                type: 'error',
                text: 'Passordene stemmer ikke overens'
            });
            setIsSubmitting(false);
            return;
        }

        // Validate password strength
        const validationErrors = validatePassword(newPassword);
        if (validationErrors.length > 0) {
            setErrors(validationErrors);
            setIsSubmitting(false);
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    token,
                    newPassword,
                    confirmPassword
                })
            });

            const data = await response.json();

            if (response.ok) {
                setMessage({
                    type: 'success',
                    text: data.message || 'Passordet ditt er tilbakestilt. Du kan nå logge inn med det nye passordet.'
                });

                // Redirect to login after 3 seconds
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else {
                if (data.errors && Array.isArray(data.errors)) {
                    setErrors(data.errors.map((err: any) => err.msg || err.message));
                } else {
                    setMessage({
                        type: 'error',
                        text: data.message || 'Kunne ikke tilbakestille passordet. Prøv igjen.'
                    });
                }
            }
        } catch (error) {
            console.error('Reset password error:', error);
            setMessage({
                type: 'error',
                text: 'Kunne ikke koble til serveren. Prøv igjen senere.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!token) {
        return (
            <div className="auth-container">
                <div className="auth-form">
                    <div className="auth-header">
                        <h2>Ugyldig lenke</h2>
                    </div>
                    <div className="auth-error">
                        Tilbakestillingslenken er ugyldig eller utløpt.
                    </div>
                    <div className="auth-link">
                        <Link to="/forgot-password">
                            Be om ny tilbakestillingslenke
                        </Link>
                    </div>
                    <div className="auth-link">
                        <Link to="/login">
                            ← Tilbake til innlogging
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-container">
            <div className="auth-form">
                <div className="auth-header">
                    <h2>Tilbakestill passord</h2>
                    <p>Velg et nytt passord for kontoen din</p>
                </div>

                <form onSubmit={handleSubmit}>
                    {message && (
                        <div className={`auth-${message.type}`}>
                            {message.text}
                        </div>
                    )}

                    {errors.length > 0 && (
                        <div className="auth-error">
                            <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                {errors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="auth-form-group">
                        <label htmlFor="newPassword" className="sr-only">
                            Nytt passord
                        </label>
                        <input
                            type="password"
                            id="newPassword"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="first-input"
                            placeholder="Nytt passord"
                            required
                            disabled={isSubmitting || message?.type === 'success'}
                        />
                    </div>

                    <div className="auth-form-group">
                        <label htmlFor="confirmPassword" className="sr-only">
                            Bekreft passord
                        </label>
                        <input
                            type="password"
                            id="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="last-input"
                            placeholder="Bekreft passord"
                            required
                            disabled={isSubmitting || message?.type === 'success'}
                        />
                    </div>

                    <div className="password-requirements">
                        <p style={{ fontSize: '12px', color: '#666', margin: '10px 0' }}>
                            Passordet må inneholde:
                        </p>
                        <ul style={{ fontSize: '12px', color: '#666', margin: '0', paddingLeft: '20px' }}>
                            <li>Minst 8 tegn</li>
                            <li>Minst én stor bokstav</li>
                            <li>Minst én liten bokstav</li>
                            <li>Minst ett tall</li>
                            <li>Minst ett spesialtegn (@$!%*?&)</li>
                        </ul>
                    </div>

                    <button
                        type="submit"
                        disabled={isSubmitting || message?.type === 'success'}
                        className="auth-submit"
                    >
                        {isSubmitting && <span className="spinner"></span>}
                        {isSubmitting ? 'Tilbakestiller...' : 'Tilbakestill passord'}
                    </button>

                    {message?.type !== 'success' && (
                        <div className="auth-link">
                            <Link to="/login">
                                ← Tilbake til innlogging
                            </Link>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default ResetPassword;
